import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentHead, FileUpload, FileWithPreview } from '../../../../lib/components';
import { ContentBox } from '../../../../lib/components/compound/pages/contentBox';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { Button } from '../../../../lib/components/atomic/button';
import { AppMockup } from '../../../../lib/components/app/mockup';
import { useCompanySettings } from '../../../../queries/company';
import { useStore } from '../../../../hooks/useStore';
import { usePostCompanyLogo } from '../../../../queries/company/mutations';
import { Base64FileFieldRequest } from '@pxtra-client-lib-legacy/gen';
import Util from '../../../../misc/util';
import { getBase64 } from '../../../../lib/util';
import { useQueryClient } from '@tanstack/react-query';

export const CompanyLogo = observer(() => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? 'MISSING_ID';

  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [fileToSubmit, setFileToSubmit] = useState<Base64FileFieldRequest | undefined | null>(
    undefined
  );
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const isInitialMount = useRef(true);

  const { data: companySettings } = useCompanySettings(companyId);
  const submitCompanyLogo = usePostCompanyLogo(companyId);

  const loadPreview = () => {
    if (!companySettings?.logo) return;
    if (isInitialMount.current) {
      return {
        name: companySettings.logo.name.replace('company_logos/', ''),
        url: companySettings.logo.url,
        size: companySettings.logo.size
      };
    }
  };

  const handleFileChange = async (value: unknown) => {
    const files = value as Array<FileWithPreview>;

    if (files.length > 1) throw new Error('Maximum number of files is 1');

    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (files.length > 0) {
        setPreviewUrl(Util.getURLfromFileWithPreview(files[0]));
      }
      return;
    }

    if (files.length == 0) {
      setPreviewUrl(undefined);
      setFileToSubmit(null);
      setCanSubmit(true);
      return;
    }

    const file = files[0];
    setPreviewUrl(Util.getURLfromFileWithPreview(file));
    setFileToSubmit({
      name: file.name,
      encoded_string: String(await getBase64(file))
    });

    setCanSubmit(true);
  };

  const handleSubmit = async () => {
    try {
      await submitCompanyLogo.mutateAsync(fileToSubmit ?? null);
      setCanSubmit(false);
      queryClient.invalidateQueries({
        queryKey: ['company', companyId, 'settings']
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t('company:COMPANY_LOGO')}
          renderActions={() => <div />}
          showActions={true}
        />
      }
    >
      <FeedbackAlert description={t('company:LOGO_NOTE')} severity="info" />
      <div className="flex gap-x-8 mt-8">
        <div className="flex flex-col gap-y-8 w-1/3">
          <div className="w-full rounded-lg">
            <FileUpload
              dropzoneWidth={'100%'}
              dropzoneHeight={'200px'}
              onChange={handleFileChange}
              options={{
                maxFiles: 1,
                accept: {
                  'image/png': ['.png'],
                  'image/svg+xml': ['.svg']
                },
                maxSize: 2 * 1024 * 1024
              }}
              showOptions={false}
              initFiles={loadPreview()}
            />
          </div>
          <div className="flex flex-col gap-y-4 bg-grey-50 rounded-lg p-4 text-sm">
            <div>{t('company:SPECIFICATIONS')}</div>
            <ul className="pl-4 m-0">
              {Object.values(t('company:LOGO_SPECIFICATIONS', { returnObjects: true })).map(
                (text, index) => (
                  <li key={index}>{text}</li>
                )
              )}
            </ul>
          </div>
          <div className="flex gap-x-2 w-full">
            <Button className="grow" disabled={!canSubmit} onClick={handleSubmit}>
              {t('common:SAVE')}
            </Button>
          </div>
        </div>
        <div className="flex flex-col grow justify-between w-2/3">
          <div className="font-medium mx-auto">{t('common:PREVIEW')}</div>
          <div className="w-full">
            <AppMockup logo={previewUrl} />
          </div>
        </div>
      </div>
    </ContentBox>
  );
});
